/** @format */

import { FC, useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  Building2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Database,
  FileText,
  Loader,
  MousePointerClick,
  ScanSearch,
} from "lucide-react";
import Header from "../../components/ui/Header";
import HeroSection from "../../components/ui/HeroSection";
import SOPListFilters, { countFilters } from "../../components/sop/SOPListFilters";
import { rmaService } from "../../api/rmaService";
import { useOrgRole } from "../../hooks/useOrgRole";
import { useOrganizationStore } from "../../store/useOrganizationStore";
import { useSopStore, type SopListFilters } from "../../store/useSopStore";
import { useSuperAdmin } from "../../hooks/useSuperAdmin";
import "../sop-dashboard/SopDashBoard.css";

const DEFAULT_LIMIT = 20;
const PAGE_SIZE_OPTIONS = [20, 50, 100];

function formatCreatedAt(isoString: string): string {
  const date = new Date(isoString.endsWith("Z") ? isoString : `${isoString}Z`);
  const locale = navigator.language;
  return (
    date.toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    }) +
    ", " +
    date.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
  );
}

interface RMASopSummary {
  criteriaTotal: number;
  passCount: number;
  failCount: number;
  lastDatasetUpdateAt: string | null;
  latestAnalysisAt: string | null;
}

const RMADashboardPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const limitParam = parseInt(searchParams.get("limit") ?? String(DEFAULT_LIMIT), 10);
  const limit = [20, 50, 100].includes(limitParam) ? limitParam : DEFAULT_LIMIT;

  const { canAccessRMA, isRoleResolving } = useOrgRole();
  const { isSuperAdmin } = useSuperAdmin();
  const { organizations } = useOrganizationStore();
  const { sops, pagination, isLoading, error, clearError, fetchSOPs } = useSopStore();
  const [appliedFilters, setAppliedFilters] = useState<SopListFilters>({});
  const [rmaSummaries, setRmaSummaries] = useState<Record<string, RMASopSummary>>({});

  const filtersForApi = useCallback(
    (f: SopListFilters): SopListFilters =>
      isSuperAdmin ? f : { ...f, organizationId: undefined },
    [isSuperAdmin],
  );

  const fetchList = useCallback(async () => {
    clearError();
    await fetchSOPs({
      page,
      limit,
      ...filtersForApi(appliedFilters),
    });
  }, [clearError, fetchSOPs, filtersForApi, appliedFilters, page, limit]);

  useEffect(() => {
    if (canAccessRMA) {
      fetchList().catch(() => {});
    }
  }, [canAccessRMA, fetchList]);

  useEffect(() => {
    let cancelled = false;

    const loadRmaSummaries = async () => {
      if (!canAccessRMA || isLoading || sops.length === 0) {
        if (!cancelled && sops.length === 0) setRmaSummaries({});
        return;
      }

      const entries = await Promise.all(
        sops.map(async (sop) => {
          try {
            const [criteria, latestResults] = await Promise.all([
              rmaService.getCriteria(sop.sopId),
              rmaService.getLatestResults(sop.sopId),
            ]);
            const passCount = criteria.filter((c) => c.verdict === "pass").length;
            const failCount = criteria.filter((c) => c.verdict === "fail").length;
            const lastDatasetUpdateAt =
              criteria
                .map((c) => c.created_at)
                .filter((value): value is string => Boolean(value))
                .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] ?? null;
            return [
              sop.sopId,
              {
                criteriaTotal: criteria.length,
                passCount,
                failCount,
                lastDatasetUpdateAt,
                latestAnalysisAt: latestResults?.created_at ?? null,
              } satisfies RMASopSummary,
            ] as const;
          } catch {
            return [
              sop.sopId,
              {
                criteriaTotal: 0,
                passCount: 0,
                failCount: 0,
                lastDatasetUpdateAt: null,
                latestAnalysisAt: null,
              } satisfies RMASopSummary,
            ] as const;
          }
        }),
      );

      if (cancelled) return;
      setRmaSummaries(Object.fromEntries(entries));
    };

    loadRmaSummaries().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [canAccessRMA, isLoading, sops]);

  const handleApplyFilters = useCallback(
    (filters: SopListFilters) => {
      setAppliedFilters(filters);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams],
  );

  const handleClearFilters = useCallback(() => {
    setAppliedFilters({});
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", "1");
      return next;
    });
  }, [setSearchParams]);

  if (isRoleResolving) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black flex items-center justify-center">
        <Loader className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  if (!canAccessRMA) {
    return <Navigate to="/sop-dashboard" replace />;
  }

  const appliedFiltersForList = isSuperAdmin
    ? appliedFilters
    : { ...appliedFilters, organizationId: undefined };

  const totalPages = pagination
    ? Math.max(1, Math.ceil(pagination.total / pagination.limit))
    : 1;
  const hasNext = pagination?.hasNext ?? false;

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-black text-white">
      <Header />

      <main className="pt-20 px-4">
        <div className="pt-8 w-full mx-auto">
          <div className="max-w-6xl mx-auto">
            <HeroSection
              title="RMA Dashboard"
              description="Browse SOPs and open RMA defect analysis or manage the RMA dataset."
              icons={[<ScanSearch className="w-12 h-12 text-cyan-400" />, null]}
            />

            <div className="bg-gradient-to-br from-gray-900/20 to-black/40 backdrop-blur-xl border border-gray-800/15 rounded-2xl p-8 shadow-2xl shadow-black/50">
              <div className="space-y-3 mb-4">
                <SOPListFilters
                  organizations={organizations}
                  onApply={handleApplyFilters}
                  onClear={handleClearFilters}
                  activeFilterCount={countFilters(appliedFiltersForList)}
                  disabled={isLoading}
                  showOrganizationFilter={isSuperAdmin}
                />
              </div>

              {error && (
                <div
                  className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-300 flex items-center justify-between mb-4"
                  role="alert"
                >
                  <span>{error}</span>
                  <button
                    type="button"
                    onClick={() => fetchList()}
                    className="ml-2 px-2 py-1 rounded border border-red-400/50 hover:bg-red-500/20"
                  >
                    Retry
                  </button>
                </div>
              )}

              {isLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="flex flex-col items-center gap-4">
                    <Loader className="animate-spin w-8 h-8 text-blue-400" />
                    <p className="text-slate-400">Loading SOPs...</p>
                  </div>
                </div>
              ) : (
                <div className="w-full">
                  <div className="grid grid-cols-12 gap-x-2 gap-y-4 p-4 bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-600/30 rounded-t-xl">
                    <div className={`flex items-center gap-2 min-w-0 ${isSuperAdmin ? "col-span-2" : "col-span-4"}`}>
                      <FileText className="w-4 h-4 text-blue-400 shrink-0" />
                      <span className="font-semibold text-white truncate">Title</span>
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <Database className="w-4 h-4 text-emerald-400 shrink-0" />
                      <span className="font-semibold text-white">Dataset</span>
                    </div>
                    {isSuperAdmin && (
                      <div className="col-span-2 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-purple-400" />
                        <span className="font-semibold text-white">Organization</span>
                      </div>
                    )}
                    <div className="col-span-2 flex items-center gap-2 min-w-0">
                      <Clock3 className="w-4 h-4 text-amber-400 shrink-0" />
                      <span className="font-semibold text-white truncate">Dataset Updated</span>
                    </div>
                    <div className="col-span-3 flex items-center gap-2 min-w-0">
                      <Calendar className="w-4 h-4 text-green-400 shrink-0" />
                      <span className="font-semibold text-white truncate">Last Analysis</span>
                    </div>
                    <div className="col-span-1 flex items-center justify-end gap-2 min-w-[40px] shrink-0">
                      <MousePointerClick className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="font-semibold text-white">Actions</span>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-slate-800/30 to-slate-900/30 border-l border-r border-b border-slate-600/30 rounded-b-xl overflow-hidden">
                    {sops.length === 0 ? (
                      <div className="text-center py-16">
                        <div className="flex flex-col items-center gap-4">
                          <div className="w-16 h-16 bg-gradient-to-br from-slate-700/50 to-slate-800/50 rounded-full flex items-center justify-center border border-slate-600/30">
                            <ScanSearch className="w-8 h-8 text-slate-400" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-white mb-2">
                              No SOPs found
                            </h3>
                            <p className="text-slate-400 max-w-md">
                              Try changing filters or add SOPs first.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-700/30">
                        {sops.map((sop) => (
                          <div
                            key={sop.sopId}
                            className="grid grid-cols-12 gap-x-2 gap-y-4 p-4 hover:bg-gradient-to-r hover:from-slate-700/20 hover:to-slate-800/20 transition-all duration-200"
                          >
                            <div
                              className={`flex items-center min-w-0 ${isSuperAdmin ? "col-span-2" : "col-span-4"}`}
                            >
                              <div className="flex items-center gap-3 w-full">
                                <div className="w-2 h-2 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full shrink-0" />
                                <p className="text-white font-medium truncate">
                                  {sop.getDisplayTitle()}
                                </p>
                              </div>
                            </div>
                            <div className="col-span-2 flex items-center">
                              <div className="flex items-center gap-2 text-xs">
                                <span className="inline-flex items-center rounded-full border border-green-500/30 bg-green-500/10 px-2 py-1 font-semibold text-green-300">
                                  Pass {rmaSummaries[sop.sopId]?.passCount ?? 0}
                                </span>
                                <span className="inline-flex items-center rounded-full border border-red-500/30 bg-red-500/10 px-2 py-1 font-semibold text-red-300">
                                  Fail {rmaSummaries[sop.sopId]?.failCount ?? 0}
                                </span>
                              </div>
                            </div>
                            {isSuperAdmin && (
                              <div className="col-span-2 flex items-center">
                                <span
                                  className="text-slate-300 text-sm truncate"
                                  title={sop.organizationName ?? undefined}
                                >
                                  {sop.organizationName ?? "—"}
                                </span>
                              </div>
                            )}
                            <div className="col-span-2 flex items-center min-w-0">
                              {rmaSummaries[sop.sopId]?.lastDatasetUpdateAt ? (
                                <span className="text-slate-300 text-sm whitespace-nowrap truncate">
                                  {formatCreatedAt(rmaSummaries[sop.sopId].lastDatasetUpdateAt!)}
                                </span>
                              ) : (
                                <span className="text-slate-500 text-xs whitespace-nowrap">
                                  Never
                                </span>
                              )}
                            </div>
                            <div className="col-span-3 flex items-center min-w-0">
                              {rmaSummaries[sop.sopId]?.latestAnalysisAt ? (
                                <span className="text-slate-300 text-sm whitespace-nowrap">
                                  {formatCreatedAt(rmaSummaries[sop.sopId].latestAnalysisAt!)}
                                </span>
                              ) : (
                                <span className="text-slate-500 text-sm whitespace-nowrap">
                                  Never
                                </span>
                              )}
                            </div>
                            <div className="col-span-1 flex items-center justify-end gap-2 min-w-[40px] shrink-0">
                              <button
                                type="button"
                                onClick={() => navigate(`/sop/${sop.sopId}/rma`)}
                                className="flex items-center gap-2 rounded-lg border border-slate-600/50 bg-slate-700/30 p-2 text-slate-300 hover:bg-slate-600/40 hover:text-white transition-colors"
                                title="Open RMA Analysis"
                              >
                                <ScanSearch className="w-4 h-4 shrink-0 text-slate-400" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {pagination && (pagination.total > 0 || totalPages > 1) && (
                    <div className="flex flex-wrap items-center justify-between gap-4 px-2 py-3 border-t border-slate-600/30 mt-4">
                      <div className="flex items-center gap-4 flex-wrap">
                        <p className="text-slate-400 text-sm">
                          Page {page} of {totalPages}
                          {pagination.total > 0 && (
                            <span className="ml-2">({pagination.total} total)</span>
                          )}
                        </p>
                        <label className="flex items-center gap-2 text-slate-400 text-sm">
                          <span>Per page:</span>
                          <select
                            value={limit}
                            onChange={(e) => {
                              setSearchParams((prev) => {
                                const next = new URLSearchParams(prev);
                                next.set("limit", String(Number(e.target.value)));
                                next.set("page", "1");
                                return next;
                              });
                            }}
                            className="rounded border border-slate-600/50 bg-slate-700/30 text-slate-200 px-2 py-1 text-sm"
                          >
                            {PAGE_SIZE_OPTIONS.map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            if (page <= 1) return;
                            setSearchParams((prev) => {
                              const next = new URLSearchParams(prev);
                              next.set("page", String(page - 1));
                              return next;
                            });
                          }}
                          disabled={page <= 1}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-600/50 bg-slate-700/30 text-slate-300 hover:bg-slate-600/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (!hasNext) return;
                            setSearchParams((prev) => {
                              const next = new URLSearchParams(prev);
                              next.set("page", String(page + 1));
                              return next;
                            });
                          }}
                          disabled={!hasNext}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-600/50 bg-slate-700/30 text-slate-300 hover:bg-slate-600/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RMADashboardPage;
